import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  postCall,
  deleteCall,
  getById,
  BASE_URL,
  getCall,
  putCall,
} from "../../utils/api";
import axios from "axios";
import toast from "react-hot-toast";

const initialState = {
  data: {
    data: [], // array of offers
    pagination: null, // optional, in case response includes pagination
  },
  offerDetail: null,
  error: null,
  status: "idle",
  isUpdateStatus: "idle", // used for update dont-miss-out item
  updateStatus: "idle", // used for update dont-miss-out list title
};

export const getDontMissOut = createAsyncThunk(
  "dontMissOut/get",
  async ({ query = "", page = 1, per_page = 50 }) => {
    try {
      const response = await getCall(
        `/admin/v1/dont-miss-out-v1.0.27?query=${query}&page=${page}&per_page=${per_page}`,
        false
      );
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

export const getDontMissOutById = createAsyncThunk(
  "dontMissOut/getById",
  async (id) => {
    try {
      const response = await getById(`/admin/v1/dont-miss-out`, id, false);
      return response;
    } catch (error) {
      toast.error(error?.response?.data.message);
      return Promise.reject(error);
    }
  }
);

export const addDontMissOut = createAsyncThunk(
  "dontMissOut/add",
  async (data) => {
    try {
      const response = await postCall("/admin/v1/dont-miss-out", data);
      return response;
    } catch (error) {
      toast.error(error?.response?.data.message);
      return Promise.reject(error);
    }
  }
);

export const updateDontMissOut = createAsyncThunk(
  "dontMissOut/update",
  async ({ id, formData }, { rejectWithValue }) => {
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const response = await axios.post(
        `${BASE_URL}/admin/v1/dont-miss-out/${id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success(
        response.data.message || "Hero banner updated successfully!"
      );
      return response.data;
    } catch (error) {
      toast.error(error?.response?.data.message);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteDontMissOut = createAsyncThunk(
  "dontMissOut/delete",
  async (id) => {
    try {
      await deleteCall(`/admin/v1/dont-miss-out/${id}`);
      return id;
    } catch (error) {
      toast.error(error?.response?.data.message);
      return Promise.reject(error);
    }
  }
);

export const dragDropDontMissOutByButton = createAsyncThunk(
  "dontMissOut/dragDropByButton",
  async ({ id, type }) => {
    try {
      const response = await postCall(`/admin/v1/dont-miss-out/${id}/${type}`);
      return response;
    } catch (error) {
      toast.error(error?.response?.data.message);
      return Promise.reject(error);
    }
  }
);

export const dragDropDontMissOut = createAsyncThunk(
  "dontMissOut/dragDrop",
  async ({ id, to }) => {
    try {
      const response = await postCall(
        `/admin/v1/dont-miss-out/${id}/direction`,
        {
          to,
        }
      );
      return response;
    } catch (error) {
      toast.error(error?.response?.data.message);
      return Promise.reject(error);
    }
  }
);

export const updateDontMissOutTitle = createAsyncThunk(
    'dontMissOut/updateTitle',
    async ({ display_name }, { rejectWithValue }) => {
      try {
        const response = await putCall('/admin/v1/dont-miss-out', {
          type: 'dont_miss_out',
          display_name
        });
        return response.data;
      } catch (error) {
        return rejectWithValue(error.response?.data || 'Failed to update title');
      }
    }
);

const dontMissOutSlice = createSlice({
  name: "dontMissOut",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Get All
      .addCase(getDontMissOut.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getDontMissOut.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.data = action.payload;
      })
      .addCase(getDontMissOut.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to fetch data.";
      })

      // Get by ID
      .addCase(getDontMissOutById.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getDontMissOutById.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.offerDetail = action.payload;
      })
      .addCase(getDontMissOutById.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to fetch detail.";
      })

      // Add
      .addCase(addDontMissOut.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(addDontMissOut.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.data.data.push(action.payload);
      })
      .addCase(addDontMissOut.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to add item.";
      })

      // Update
      .addCase(updateDontMissOut.pending, (state) => {
        state.isUpdateStatus = "loading";
        state.error = null;
      })
      .addCase(updateDontMissOut.fulfilled, (state, action) => {
        state.isUpdateStatus = "succeeded";
        const index = state.data.data.findIndex(
          (item) => item.id === action.payload.data.id
        );
        if (index !== -1) {
          state.data.data[index] = action.payload.data;
        }
      })
      .addCase(updateDontMissOut.rejected, (state, action) => {
        state.isUpdateStatus = "failed";
        state.error = action.payload || "Failed to update item.";
      })

      // Delete
      .addCase(deleteDontMissOut.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(deleteDontMissOut.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.data.data = state.data.data.filter(
          (item) => item.id !== action.payload
        );
      })
      .addCase(deleteDontMissOut.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to delete item.";
      })

      // Drag and Drop
      .addCase(dragDropDontMissOut.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(dragDropDontMissOut.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.data.data = action.payload.data;
      })
      .addCase(dragDropDontMissOut.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to drag and drop item.";
      })

      // Drag and Drop by Button
      .addCase(dragDropDontMissOutByButton.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(dragDropDontMissOutByButton.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.data.data = action.payload.data;
      })
      .addCase(dragDropDontMissOutByButton.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to drag and drop item.";
      })
      .addCase(updateDontMissOutTitle.pending, (state) => {
        state.updateStatus = 'loading';
      })
      .addCase(updateDontMissOutTitle.fulfilled, (state, action) => {
        state.updateStatus = 'succeeded';
        if (state.data) {
          state.data.display_name = action.payload.display_name;
        }
      })
      .addCase(updateDontMissOutTitle.rejected, (state, action) => {
        state.updateStatus = 'failed';
        state.error = action.payload;
      });
  },
});

export default dontMissOutSlice.reducer;
